import { overviewConstants } from '../../constants/appConstants';

import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

type Role = { id: string; label: string; mappedTSRoleId: string };

export const invitePeopleUsername = 'username';
export const invitePeopleFirstName = 'firstName';
export const invitePeopleLastName = 'lastName';
export const invitePeopleRoleId = 'roleId';
export const invitePeopleApproverId = 'approverId';

export type InvitePeople = {
	username: string;
	firstName: string;
	lastName: string;
	roleId: string;
	approverId?: string | undefined;
	teamId: string;
	companyAccountId: string;
};

export type InvitePeopleWithPhoneNumber = InvitePeople & {
	phoneNumber: string;
	phoneCountryCode: string;
	phoneNumberValue: string; // used in ui only to set the value on inupt
};

type InvitePeopleState = {
	invitePeople: InvitePeopleWithPhoneNumber;
};

type InvitePeopleActions = {
	invitePeopleReset: (teamId: string) => void;
	invitePeopleSetUsername: (username: InvitePeopleState['invitePeople']['username']) => void;
	invitePeopleSetPhoneNumber: (payload: {
		phoneNumber: string;
		phoneCountryCode: string;
		phoneNumberValue: string;
	}) => void;
	invitePeopleSetFirstName: (firstName: InvitePeopleState['invitePeople']['firstName']) => void;
	invitePeopleSetLastName: (lastName: InvitePeopleState['invitePeople']['lastName']) => void;
	invitePeopleSetRoleId: (roleId: InvitePeopleState['invitePeople']['roleId']) => void;
	invitePeopleSetApproverId: (approverId: InvitePeopleState['invitePeople']['approverId']) => void;
	invitePeopleSetTeamId: (teamId: InvitePeopleState['invitePeople']['teamId']) => void;
	invitePeopleSetCompanyAccountId: (companyAccountId: InvitePeopleState['invitePeople']['companyAccountId']) => void;
};

export type InvitePeopleSlice = InvitePeopleState & InvitePeopleActions;

const getInvitePeopleDefaultState = (teamId: Role['mappedTSRoleId'] = '') => ({
	username: '',
	firstName: '',
	lastName: '',
	roleId: overviewConstants.COMPANY_ADMIN,
	approverId: undefined,
	teamId,
	companyAccountId: '',
	phoneNumber: '',
	phoneCountryCode: '',
	phoneNumberValue: '',
});

const invitePeopleInitialState: InvitePeopleState = {
	invitePeople: getInvitePeopleDefaultState(),
};

export function invitePeopleSlice(set: NamedSet<ZustandStore>): InvitePeopleSlice {
	return {
		...invitePeopleInitialState,
		invitePeopleReset(teamId: string): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople = getInvitePeopleDefaultState(teamId);
					return state;
				},
				false,
				'[invitePeopleReset]',
			);
		},
		invitePeopleSetUsername(username: InvitePeopleState['invitePeople']['username']): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.username = username;
					return state;
				},
				false,
				'[setInvitePeopleUsername]',
			);
		},
		invitePeopleSetPhoneNumber(payload: {
			phoneNumber: string;
			phoneCountryCode: string;
			phoneNumberValue: string;
		}): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.phoneNumber = payload.phoneNumber;
					state.invitePeople.phoneCountryCode = payload.phoneCountryCode;
					state.invitePeople.phoneNumberValue = payload.phoneNumberValue;
					return state;
				},
				false,
				'[setInvitePeoplePhoneNumber]',
			);
		},
		invitePeopleSetFirstName(firstName: InvitePeopleState['invitePeople']['firstName']): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.firstName = firstName;
					return state;
				},
				false,
				'[setInvitePeopleFirstName]',
			);
		},
		invitePeopleSetLastName(lastName: InvitePeopleState['invitePeople']['lastName']): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.lastName = lastName;
					return state;
				},
				false,
				'[setInvitePeopleLastName]',
			);
		},
		invitePeopleSetRoleId(roleId: InvitePeopleState['invitePeople']['roleId']): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.roleId = roleId;
					return state;
				},
				false,
				'[setInvitePeopleRoleId]',
			);
		},

		invitePeopleSetApproverId(approverId: InvitePeopleState['invitePeople']['approverId']): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.approverId = approverId;
					return state;
				},
				false,
				'[setInvitePeopleApproverId]',
			);
		},
		invitePeopleSetTeamId(teamId: InvitePeopleState['invitePeople']['teamId']): void {
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.teamId = teamId;
					return state;
				},
				false,
				'[setInvitePeopleTeamId]',
			);
		},
		invitePeopleSetCompanyAccountId(companyAccountId: InvitePeopleState['invitePeople']['companyAccountId']): void {
			// ! check if we need
			set(
				(state: InvitePeopleState) => {
					state.invitePeople.companyAccountId = companyAccountId;
					return state;
				},
				false,
				'[setInvitePeopleCompanyAccountId]',
			);
		},
	};
}
